import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  X as XMarkIcon,
  FileText as DocumentCheckIcon,
  CheckCircle,
  Clock,
  Server,
  Loader2 as LoaderIcon
} from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
const GenerateReportModal = ({ isOpen, onClose, onReportGenerated }) => {
  const [urlId, setUrlId] = useState('');
  const [scanId, setScanId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState('input'); // input, generating, success
  const [availableScans, setAvailableScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch available scans when modal opens
  useEffect(() => {
    if (isOpen) fetchAvailableScans();
  }, [isOpen]);

  const fetchAvailableScans = async () => {
    try {
      setLoading(true);
      const response = await fetch(ApiNames.SCANS);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAvailableScans(data.scans || []);
    } catch (error) {
      console.error('Error fetching scans:', error);
      toast.error('Failed to fetch available scans');
      setAvailableScans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scanId.trim()) {
      toast.error('Please select a scan from the dropdown');
      return;
    }

    setGenerating(true);
    setStep('generating');

    try {
      const endpoint = ApiNames.GENERATE_REPORT_FROM_SCAN(scanId);

      const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' } });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedReport(data);
      setStep('success');

      // Auto-close after 2 seconds
      setTimeout(() => {
        onReportGenerated(data.scan_id);
      }, 2000);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Failed to generate report. Please try again.');
      setStep('input');
    } finally {
      setGenerating(false);
    }
  };

  const handleScanSelect = (scan) => {
    setSelectedScan(`${scan.url_name} (URL ID: ${scan.url_id}) - Scan: ${scan.scan_id.substring(0, 8)}...`);
    setUrlId(scan.url_id.toString());
    setScanId(scan.scan_id);
  };

  const resetForm = () => {
    setUrlId('');
    setScanId('');
    setSelectedScan('');
    setGeneratedReport(null);
    setStep('input');
    setGenerating(false);
    setAvailableScans([]);
    setLoading(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Don't render modal if not open
  if (!isOpen) return null;

  return (
    <div className="">
      <div class="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm z-9" style={{ zIndex: 90 }}></div>
      <div className="fixed inset-0 overflow-y-auto z-99" style={{ zIndex: 99 }}>
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[--ds-secondary-bg] p-6 text-left align-middle shadow-xl transition-all">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-[--ds-text-900] flex items-center">
                <DocumentCheckIcon className="w-5 h-5 mr-2 text-primary-600" />
                Generate Compliance Report
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg p-2 text-[--ds-text-400] hover:text-[--ds-text-500] hover:bg-[--ds-gray-100-bg] transition-colors duration-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            {step === 'input' && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[--ds-text-700] mb-3">
                    Select URL and Scan for Report Generation
                  </label>

                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoaderIcon className="w-6 h-6 text-primary-600 animate-spin" />
                      <span className="ml-2 text-[--ds-text-600]">Loading available scans...</span>
                    </div>
                  ) : availableScans.length === 0 ? (
                    <p className="text-[--ds-text-500] text-center py-8">No scans available.</p>
                  ) : (
                    <div className="space-y-3">
                      <select
                        value={selectedScan}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          if (!selectedValue) return setSelectedScan('');
                          const scan = availableScans.find(
                            s => `${s.url_name} (URL ID: ${s.url_id}) - Scan: ${s.scan_id.substring(0, 8)}...` === selectedValue
                          );
                          if (scan) handleScanSelect(scan);
                        }}
                        className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select a URL and Scan...</option>
                        {availableScans.map(scan => (
                          <option key={scan.scan_id} value={`${scan.url_name} (URL ID: ${scan.url_id}) - Scan: ${scan.scan_id.substring(0, 8)}...`}>
                            {scan.url_name} (URL ID: {scan.url_id}) - Scan: {scan.scan_id.substring(0, 8)}... {scan.is_latest ? '(Latest)' : scan.has_report ? '(Has Report)' : '(New)'}
                          </option>
                        ))}
                      </select>
                      {selectedScan && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-blue-800 mb-2">Selected Scan Details</h4>
                              <div className="space-y-1 text-sm text-blue-700">
                                {(() => {
                                  const scan = availableScans.find(s => s.url_id.toString() === urlId);
                                  return scan ? (
                                    <>
                                      <div className="flex items-center">
                                        <Server className="w-4 h-4 mr-2" />
                                        URL: {scan.url_name} (ID: {scan.url_id})
                                      </div>
                                      <div className="flex items-center">
                                        <DocumentCheckIcon className="w-4 h-4 mr-2" />
                                        Scan ID: {scan.scan_id}
                                      </div>
                                      <div className="flex items-center">
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Status: {scan.has_report ? 'Has Report' : 'New Scan'}
                                      </div>
                                    </>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                            )}
                            {selectedScan && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-green-800 mb-2">Ready to Generate Report</h4>
                        <p className="text-sm text-green-700">
                          A new compliance report will be generated based on the selected scan data.
                          This will analyze the current compliance status and provide updated metrics.
                        </p>
                      </div>
                    )}
                    </div>
                  )}
                </div>
                

                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={handleClose} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={!selectedScan || generating} className="btn-primary flex items-center">
                    <DocumentCheckIcon className="w-4 h-4 mr-2" /> Generate Report
                  </button>
                </div>
              </form>
            )}

            {step === 'generating' && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                      <LoaderIcon className="w-8 h-8 text-primary-600 animate-spin" />
                    </div>
                    <h3 className="text-lg font-medium text-[--ds-text-900] mb-2">Generating Report</h3>
                    <p className="text-[--ds-text-600] mb-4">
                      Processing compliance frameworks and analyzing scan data...
                    </p>
                    <div className="space-y-2 text-sm text-left">
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        USWDS Compliance Framework
                      </div>
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        OMB M-15-13 HTTPS Framework
                      </div>
                      <div className="flex items-center text-[--ds-text-400]">
                        <div className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                        Calculating overall metrics...
                      </div>
                    </div>
              </div>
            )}

            {step === 'success' && generatedReport && (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-success-600 mx-auto mb-4" />
                <p className="text-[--ds-text-900] font-medium mb-2">Report Generated Successfully!</p>
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <p className='text-[--ds-black]'>Report ID: {generatedReport.report_id}</p>
                  <p className='text-[--ds-black]'>Scan ID: {generatedReport.scan_id}</p>
                  <p className='text-[--ds-black]'>Frameworks: {generatedReport.frameworks_processed}</p>
                  <p className='text-[--ds-black]'>Overall Compliance: {generatedReport.overall_compliance}%</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateReportModal;
